/**
 * ShopArticlePage — Phase 4e.F+ (Feb 2026)
 *
 * Öffentliche Artikel-Detail-Seite unter `/shop/:dealerSlug/article/:articleId`.
 * Enthält Foto-Galerie, Produkt-Daten, Anfrage-Modal sowie volles
 * Schema.org-Product+Offer-JSON-LD via `useShopSeo`.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShoppingBag, MessageSquare, X, Tag, Truck, MapPin } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import useShopSeo from "../lib/useShopSeo";

const API = process.env.REACT_APP_BACKEND_URL;

function ShopPhoto({ path, alt = "" }) {
    const [err, setErr] = useState(false);
    if (err || !path) {
        return <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300"><ShoppingBag className="w-12 h-12" /></div>;
    }
    return <img src={`${API}/api/shop/photo/${path}`} alt={alt} className="w-full h-full object-cover" onError={() => setErr(true)} />;
}

export default function ShopArticlePage() {
    const { dealerSlug, articleId } = useParams();
    const navigate = useNavigate();
    const [shop, setShop] = useState(null);
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activePhoto, setActivePhoto] = useState(0);
    const [showEnquiry, setShowEnquiry] = useState(false);
    const [similar, setSimilar] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/api/shop/${dealerSlug}/article/${articleId}`);
                if (cancelled) return;
                setShop(r.data.shop);
                setArticle(r.data.article);
                setActivePhoto(0);
                // Phase 4e.F++ — Ähnliche Artikel parallel laden
                axios.get(`${API}/api/shop/${dealerSlug}/article/${articleId}/similar`)
                    .then((rs) => { if (!cancelled) setSimilar(rs.data.articles || []); })
                    .catch(() => {});
            } catch (e) {
                toast.error(e.response?.status === 404 ? "Artikel nicht verfügbar" : "Fehler beim Laden");
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [dealerSlug, articleId]);

    useShopSeo({ shop, articles: null, articleDetail: article });

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#FFCC00]" /></div>;
    }
    if (!article || !shop) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-3">
                <ShoppingBag className="w-12 h-12 text-gray-300" />
                <div className="text-lg font-bold">Artikel nicht verfügbar</div>
                <Link to={`/shop/${dealerSlug}`} className="text-blue-600 underline">Zurück zum Shop</Link>
            </div>
        );
    }

    const photos = article.photo_paths || [];
    const inStock = (article.stock_qty || 0) > 0;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="shop-article-page">
            {/* Breadcrumb */}
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 text-xs text-gray-600">
                <button onClick={() => navigate(`/shop/${dealerSlug}`)} className="inline-flex items-center gap-1 hover:text-[#111827]" data-testid="shop-article-back">
                    <ArrowLeft className="w-3.5 h-3.5" />
                    {shop.company_name || dealerSlug}
                </button>
                <span className="text-gray-300">/</span>
                <span className="truncate">{article.name}</span>
            </div>

            <main className="max-w-6xl mx-auto px-4 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
                    {/* Photo Gallery */}
                    <div className="space-y-3" data-testid="shop-article-gallery">
                        <div className="relative aspect-square bg-white border border-gray-200 rounded-sm overflow-hidden">
                            <ShopPhoto path={photos[activePhoto]} alt={article.name} />
                            {article.ai_generated_images && (
                                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5" data-testid="shop-article-ai-badge">
                                    KI-generiert
                                </span>
                            )}
                        </div>
                        {photos.length > 1 && (
                            <div className="grid grid-cols-6 gap-2">
                                {photos.slice(0, 6).map((p, idx) => (
                                    <button
                                        key={p}
                                        onClick={() => setActivePhoto(idx)}
                                        className={`aspect-square border-2 rounded-sm overflow-hidden ${idx === activePhoto ? "border-[#111827]" : "border-gray-200"}`}
                                        data-testid={`shop-article-thumb-${idx}`}
                                    >
                                        <ShopPhoto path={p} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info Panel */}
                    <div className="space-y-4">
                        <div>
                            {article.manufacturer && (
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500" data-testid="shop-article-brand">{article.manufacturer}</div>
                            )}
                            <h1 className="font-display text-2xl font-black tracking-tight mt-1" data-testid="shop-article-name">{article.name}</h1>
                            <div className="text-xs text-gray-500 mt-1 font-mono-num">
                                SKU: {article.sku}
                                {article.manufacturer_part_number && <> · Hersteller-Nr.: {article.manufacturer_part_number}</>}
                                {article.ean && <> · EAN: {article.ean}</>}
                            </div>
                        </div>

                        {/* Price block */}
                        <div className="bg-white border border-gray-200 rounded-sm p-4">
                            <div className="flex items-baseline justify-between">
                                <div className="font-mono-num font-black text-3xl text-[#111827]" data-testid="shop-article-price">
                                    {article.price_gross > 0 ? `${article.price_gross.toFixed(2)} €` : <span className="text-gray-400 text-xl">Auf Anfrage</span>}
                                </div>
                                {article.list_price_gross > 0 && article.list_price_gross > article.price_gross && (
                                    <div className="text-xs text-gray-500 line-through font-mono-num">UVP {article.list_price_gross.toFixed(2)} €</div>
                                )}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">inkl. MwSt., zzgl. Versand</div>
                            <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold ${inStock ? "text-emerald-700" : "text-amber-700"}`}>
                                <span className={`w-2 h-2 rounded-full ${inStock ? "bg-emerald-500" : "bg-amber-500"}`} />
                                {inStock ? `Sofort lieferbar (${article.stock_qty} ${article.unit || "Stück"})` : "Auf Anfrage"}
                            </div>
                            {/* Phase 4e.F++ — Social-Proof: Restbestand-Warnung wenn ≤ 3 Stk */}
                            {inStock && article.stock_qty <= 3 && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-sm" data-testid="shop-article-low-stock">
                                    🔥 Nur noch {article.stock_qty} {article.unit || "Stück"} verfügbar
                                </div>
                            )}
                            {/* Phase 4e.H — Social-Proof: View-Count letzte 24h */}
                            {article.views_24h > 1 && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-1 rounded-sm" data-testid="shop-article-view-count">
                                    👁 <strong className="font-mono-num">{article.views_24h}</strong>-mal in den letzten 24h angesehen
                                </div>
                            )}
                            <Button
                                onClick={() => setShowEnquiry(true)}
                                className="w-full mt-3 rounded-sm bg-[#FFCC00] hover:bg-[#FFD633] text-[#111827] font-bold"
                                data-testid="shop-article-enquiry-btn"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Anfrage senden
                            </Button>
                        </div>

                        {/* Dealer Card */}
                        <div className="bg-white border border-gray-200 rounded-sm p-3 text-xs" data-testid="shop-article-dealer-card">
                            <div className="font-bold text-sm">{shop.company_name}</div>
                            <div className="text-gray-600 mt-1 space-y-0.5">
                                {shop.city && <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{shop.city}</div>}
                                {shop.verified && <span className="ml-2 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase">Verifiziert</span>}
                            </div>
                            <Link to={`/shop/${dealerSlug}`} className="text-blue-600 hover:underline text-[11px] mt-2 inline-block">Alle Artikel ansehen →</Link>
                        </div>
                    </div>
                </div>

                {/* Description */}
                {article.description && (
                    <div className="mt-6 bg-white border border-gray-200 rounded-sm p-5" data-testid="shop-article-description">
                        <h2 className="font-display font-bold text-base mb-2 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-[#FFCC00]" />
                            Beschreibung
                        </h2>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{article.description}</div>
                        {article.ai_generated_description && (
                            <div className="mt-3 text-[11px] text-gray-500 border-t border-gray-100 pt-2" data-testid="shop-article-ai-text-label">
                                ⓘ Diese Produktbeschreibung wurde mit Unterstützung künstlicher Intelligenz erstellt (KI-generierter Inhalt).
                            </div>
                        )}
                    </div>
                )}
                {article.ai_generated_images && (
                    <div className="mt-3 text-[11px] text-gray-500" data-testid="shop-article-ai-image-label">
                        ⓘ Produktbilder teilweise KI-generiert.
                    </div>
                )}

                {/* Phase 4e.F++ — Ähnliche Artikel */}
                {similar.length > 0 && (
                    <div className="mt-8" data-testid="shop-article-similar">
                        <h2 className="font-display font-bold text-base mb-3">Ähnliche Artikel im Shop</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            {similar.map((a) => (
                                <Link
                                    to={`/shop/${dealerSlug}/article/${a.id}`}
                                    key={a.id}
                                    className="bg-white border border-gray-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow"
                                    data-testid={`shop-similar-${a.id}`}
                                >
                                    <div className="aspect-square bg-gray-50">
                                        <ShopPhoto path={(a.photo_paths || [])[0]} alt={a.name} />
                                    </div>
                                    <div className="p-2">
                                        <div className="text-xs font-bold line-clamp-2">{a.name}</div>
                                        <div className="text-xs font-mono-num font-black mt-1">
                                            {a.price_gross > 0 ? `${a.price_gross.toFixed(2)} €` : <span className="text-gray-400">Anfrage</span>}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            {showEnquiry && (
                <EnquiryModal
                    article={article}
                    dealerSlug={dealerSlug}
                    onClose={() => setShowEnquiry(false)}
                />
            )}
        </div>
    );
}

function EnquiryModal({ article, dealerSlug, onClose }) {
    const [form, setForm] = useState({ visitor_name: "", visitor_email: "", visitor_phone: "", message: `Ich interessiere mich für "${article.name}" (SKU ${article.sku}).` });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.visitor_name.trim() || !form.visitor_email.trim() || !form.message.trim()) {
            toast.error("Bitte Name, E-Mail und Nachricht ausfüllen");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/api/shop/enquiry`, {
                dealer_slug: dealerSlug, article_id: article.id, ...form,
            });
            toast.success("Anfrage gesendet — der Händler meldet sich in Kürze!");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="shop-article-enquiry-modal">
            <div className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg">Anfrage</h2>
                    <button onClick={onClose} data-testid="enquiry-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name *</Label>
                        <Input value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-name" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">E-Mail *</Label>
                        <Input type="email" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-email" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Telefon (optional)</Label>
                        <Input value={form.visitor_phone} onChange={(e) => setForm({ ...form, visitor_phone: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-phone" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht *</Label>
                        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm mt-1" rows={4} data-testid="enquiry-message" />
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm" data-testid="enquiry-cancel">Abbrechen</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="enquiry-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                        Anfrage senden
                    </Button>
                </div>
            </div>
        </div>
    );
}
